import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Widget from './Widget';
import { listJournalEntries } from '../../api/api';
import { useAccountData } from '../../context/AccountDataProvider';

/**
 * JournalWidget - Displays trade journal entries
 * 
 * Props:
 * - hideTitle: boolean - hide widget title
 * - accountId: string - filter by account
 * - limit: number - max entries to display
 */
const JournalWidget = ({ 
  hideTitle = false,
  accountId: propAccountId = null,
  limit = 20
}) => {
  const { selectedAccountId } = useAccountData();
  const accountId = propAccountId || selectedAccountId;
  
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { limit };
      if (accountId) params.account_id = accountId;
      const data = await listJournalEntries(params);
      const entriesArray = Array.isArray(data) ? data : (data?.results || []);
      setEntries(entriesArray.slice(0, limit));
    } catch (err) {
      console.error('Error fetching journal entries:', err);
      setError('Failed to load journal entries');
    } finally {
      setLoading(false);
    }
  }, [accountId, limit]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getEmotionColor = (emotion) => {
    if (!emotion) return 'bg-gray-500';
    const e = emotion.toLowerCase();
    if (e.includes('confident') || e.includes('calm') || e.includes('focused')) return 'bg-success';
    if (e.includes('anxious') || e.includes('fear') || e.includes('nervous')) return 'bg-yellow-500';
    if (e.includes('frustrated') || e.includes('angry') || e.includes('revenge')) return 'bg-destructive';
    return 'bg-gray-500';
  };

  const getActionBadgeColor = (action) => {
    if (!action) return 'bg-gray-500/20 text-gray-500';
    const a = action.toLowerCase();
    if (a.includes('open')) return 'bg-success/20 text-success';
    if (a.includes('close')) return 'bg-destructive/20 text-destructive';
    if (a.includes('modify') || a.includes('update')) return 'bg-primary/20 text-primary';
    return 'bg-gray-500/20 text-gray-500';
  };

  return (
    <Widget title="Trade Journal" hideTitle={hideTitle} className="h-full">
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-2 py-1 border-b border-borderDefault">
          <span className="text-xs text-textSecondary">
            {entries.length} entries
          </span>
          <button
            onClick={fetchEntries}
            disabled={loading}
            className="text-xs text-primary hover:text-primary/80 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {error && (
          <div className="p-4 text-center text-destructive text-sm">{error}</div>
        )}

        {!error && entries.length === 0 && !loading && (
          <div className="p-4 text-center text-textSecondary text-sm">No journal entries</div>
        )}

        {/* Journal entries list */}
        <div className="flex-1 overflow-y-auto">
          {entries.map((entry, idx) => (
            <div
              key={entry.id || idx}
              className="border-b border-borderDefault"
            >
              {/* Entry header - clickable */}
              <div 
                className="p-2 sm:p-3 hover:bg-secondary/10 cursor-pointer"
                onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
              >
                <div className="flex items-start justify-between gap-1 sm:gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                      <span className={`px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium ${getActionBadgeColor(entry.action)}`}>
                        {entry.action || 'Entry'}
                      </span>
                      {entry.strategy_tag && (
                        <span className="px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs bg-secondary/20 text-textSecondary truncate max-w-[80px] sm:max-w-none">
                          {entry.strategy_tag}
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-textPrimary line-clamp-2">
                      {entry.reason || entry.notes || 'No reason provided'}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[10px] sm:text-xs text-textSecondary">
                      {formatDate(entry.created_at)}
                    </div>
                    {entry.emotional_state && (
                      <div className="mt-1 flex items-center justify-end gap-1">
                        <span className={`w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full ${getEmotionColor(entry.emotional_state)}`}></span>
                        <span className="text-[10px] sm:text-xs text-textSecondary hidden sm:inline">{entry.emotional_state}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded details */}
              {expandedId === entry.id && (
                <div className="px-3 pb-3 pt-0 bg-secondary/5">
                  {entry.details && (
                    <div className="mb-2">
                      <div className="text-xs text-textSecondary mb-1">Details</div>
                      <p className="text-sm text-textPrimary">{entry.details}</p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {entry.trade_timeframe && (
                      <div>
                        <span className="text-textSecondary">Timeframe: </span>
                        <span className="text-textPrimary">{entry.trade_timeframe}</span>
                      </div>
                    )}
                    {entry.market_condition && (
                      <div>
                        <span className="text-textSecondary">Market: </span>
                        <span className="text-textPrimary">{entry.market_condition}</span>
                      </div>
                    )}
                  </div>

                  {/* Attachments */}
                  {entry.attachments && entry.attachments.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs text-textSecondary mb-1">Attachments ({entry.attachments.length})</div>
                      <div className="flex gap-2 flex-wrap">
                        {entry.attachments.map((att, i) => (
                          <a
                            key={i}
                            href={att.url || att}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline"
                          >
                            📎 {att.name || `Attachment ${i + 1}`}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Widget>
  );
};

JournalWidget.propTypes = {
  hideTitle: PropTypes.bool,
  accountId: PropTypes.string,
  limit: PropTypes.number,
};

export default JournalWidget;
